import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

/**
 * Hook for managing Students in Finance context
 *
 * Provides functions to:
 * - Fetch all students with filters
 * - Fetch users for linking
 * - Link/unlink/update student parent account
 */
export const useStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [serverStats, setServerStats] = useState(null);

  /**
   * Fetch all students with optional filters.
   * When the search text contains multiple words, fetches each word separately
   * and returns only students that match ALL words (intersection).
   */
  const fetchStudents = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const page = params.page || 1;
        const pageSize = params.pageSize || 10;

        const baseFilters = {};
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              baseFilters[key] = value;
            }
          });
        }

        const searchText = (baseFilters.search || "").trim();
        const words = searchText.split(/\s+/).filter(Boolean);

        if (words.length > 1) {
          // Multi-word search: fetch all matching students per word, then intersect
          const fetchAllForWord = async (word) => {
            const qs = new URLSearchParams({
              ...baseFilters,
              search: word,
              pageSize: 9999,
            }).toString();
            const url = `${domainSettings.urls.enrollment.students}?${qs}`;
            const response = await Utilities.fetchWithAuth(
              "GET",
              url,
              null,
              navigate,
            );
            if (!response.ok) return [];
            const data = response.data || response;
            return Array.isArray(data) ? data : data.results || [];
          };

          const perWordResults = await Promise.all(words.map(fetchAllForWord));

          // Short-circuit if any word has zero matches
          if (perWordResults.some((r) => r.length === 0)) {
            setStudents([]);
            setPagination({ current: 1, pageSize, total: 0 });
            return;
          }

          // Build intersection by unique student identifier
          const getId = (s) => s.spc || s.spcCode || s.id;
          const idSets = perWordResults.map((arr) => new Set(arr.map(getId)));
          const intersectedIds = idSets.reduce(
            (a, b) => new Set([...a].filter((x) => b.has(x))),
          );
          const intersected = perWordResults[0].filter((s) =>
            intersectedIds.has(getId(s)),
          );

          setStudents(intersected);
          setPagination({ current: 1, pageSize, total: intersected.length });
          return;
        }

        // Normal single-word / no-search path
        const queryObj = { page, pageSize, ...baseFilters };
        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.enrollment.students}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setStudents(Array.isArray(data) ? data : data.results || []);
          if (data.count !== undefined) {
            setPagination({ current: page, pageSize, total: data.count });
          } else if (Array.isArray(data)) {
            setPagination({ current: page, pageSize, total: data.length });
          } else {
            setPagination((prev) => ({ ...prev, current: page, pageSize }));
          }
          if (data.stats) {
            setServerStats(data.stats);
          }
        } else {
          Utilities.showError(response, "Failed to fetch students");
        }
      } catch (error) {
        message.error("An error occurred while fetching students");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Fetch all users (for linking dropdown and filtering)
   * @param {boolean|null} isStaff - Filter by staff status. null = all users
   */
  const fetchUsers = useCallback(
    async (isStaff = null) => {
      setUsersLoading(true);
      try {
        let url = domainSettings.urls.auth.listUsers;
        if (isStaff !== null) {
          url += `?is_staff=${isStaff}`;
        }
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setUsers(Array.isArray(data) ? data : []);
          return Array.isArray(data) ? data : [];
        } else {
          Utilities.showError(response, "Failed to fetch users");
          return [];
        }
      } catch (error) {
        message.error("An error occurred while fetching users");
        console.error(error);
        return [];
      } finally {
        setUsersLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Link a student to a parent account
   * @param {number} studentId - Student ID
   * @param {number} userId - User ID to link
   */
  const linkStudent = useCallback(
    async (studentId, userId) => {
      setLinkLoading(true);
      try {
        const url = domainSettings.urls.enrollment.studentLink.replace(
          ":id",
          studentId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { userID: userId },
          navigate,
        );

        if (response.ok) {
          message.success(
            response.data?.message || "Student linked successfully",
          );
          // Refresh the students list
          fetchStudents();
          return response.data;
        } else {
          Utilities.showError(
            response,
            response.data?.error || "Failed to link student",
          );
          return null;
        }
      } catch (error) {
        message.error("An error occurred while linking student");
        console.error(error);
        return null;
      } finally {
        setLinkLoading(false);
      }
    },
    [navigate, fetchStudents],
  );

  /**
   * Unlink a student from their parent account
   * @param {number} studentId - Student ID
   */
  const unlinkStudent = useCallback(
    async (studentId) => {
      setLinkLoading(true);
      try {
        const url = domainSettings.urls.enrollment.studentLink.replace(
          ":id",
          studentId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success(
            response.data?.message || "Student unlinked successfully",
          );
          // Refresh the students list
          fetchStudents();
          return true;
        } else {
          Utilities.showError(
            response,
            response.data?.error || "Failed to unlink student",
          );
          return false;
        }
      } catch (error) {
        message.error("An error occurred while unlinking student");
        console.error(error);
        return false;
      } finally {
        setLinkLoading(false);
      }
    },
    [navigate, fetchStudents],
  );

  /**
   * Update the parent account link for a student
   * @param {number} studentId - Student ID
   * @param {number} userId - New User ID to link
   */
  const updateStudentLink = useCallback(
    async (studentId, userId) => {
      setLinkLoading(true);
      try {
        const url = domainSettings.urls.enrollment.studentLink.replace(
          ":id",
          studentId,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          { userID: userId },
          navigate,
        );

        if (response.ok) {
          message.success(
            response.data?.message ||
              "Parent account link updated successfully",
          );
          // Refresh the students list
          fetchStudents();
          return response.data;
        } else {
          Utilities.showError(
            response,
            response.data?.error || "Failed to update link",
          );
          return null;
        }
      } catch (error) {
        message.error("An error occurred while updating link");
        console.error(error);
        return null;
      } finally {
        setLinkLoading(false);
      }
    },
    [navigate, fetchStudents],
  );

  return {
    students,
    users,
    loading,
    usersLoading,
    linkLoading,
    pagination,
    serverStats,
    setPagination,
    fetchStudents,
    fetchUsers,
    linkStudent,
    unlinkStudent,
    updateStudentLink,
  };
};

export default useStudents;
